/** Small typed DOM helpers, so modules are not full of casts. */

/**
 * Guard against a selector matching a document-level element.
 *
 * `<body>` carries server state in data attributes. If a component selector
 * ever collides with one of those names, a renderer that sets `textContent`
 * would erase the entire page. This turns that catastrophic, silent failure
 * into a loud one during development.
 */
function assertNotRoot(node: Element | null, selector: string): void {
  if (import.meta.env.DEV && (node === document.body || node === document.documentElement)) {
    console.error(
      `Selector "${selector}" matched <${node.tagName.toLowerCase()}>. ` +
        'A component selector must never match body/html — rename the attribute.',
    )
  }
}

/** Query a single element, typed. Returns null when absent. */
export function $<T extends Element = HTMLElement>(
  selector: string,
  scope: ParentNode = document,
): T | null {
  const node = scope.querySelector<T>(selector)
  assertNotRoot(node, selector)

  return node
}

/** Query all matching elements as a real array. */
export function $$<T extends Element = HTMLElement>(
  selector: string,
  scope: ParentNode = document,
): T[] {
  const nodes = Array.from(scope.querySelectorAll<T>(selector))
  nodes.forEach((node) => assertNotRoot(node, selector))

  return nodes
}

/** Add a listener and get back a function that removes it. */
export function on<K extends keyof HTMLElementEventMap>(
  target: EventTarget,
  type: K | string,
  handler: (event: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void {
  const listener = handler as EventListener
  target.addEventListener(type, listener, options)

  return () => target.removeEventListener(type, listener, options)
}

/**
 * Event delegation — bind once on a container instead of per element.
 *
 * Essential here because product cards are re-rendered by filters; a delegated
 * handler keeps working across DOM replacement, a direct one does not.
 */
export function delegate<K extends keyof HTMLElementEventMap>(
  root: ParentNode & EventTarget,
  type: K,
  selector: string,
  handler: (event: HTMLElementEventMap[K], match: HTMLElement) => void,
): () => void {
  const listener = (event: Event) => {
    const target = event.target
    if (!(target instanceof Element)) return

    const match = target.closest<HTMLElement>(selector)
    if (match && root.contains(match)) {
      handler(event as HTMLElementEventMap[K], match)
    }
  }

  root.addEventListener(type, listener)

  return () => root.removeEventListener(type, listener)
}

/** Create an element with attributes and children in one call. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)

  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'class') node.className = value
    else if (key === 'html') node.innerHTML = value
    else node.setAttribute(key, value)
  }

  children.forEach((child) => {
    node.append(typeof child === 'string' ? document.createTextNode(child) : child)
  })

  return node
}

/** Debounce — used for the search-as-you-type input. */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  wait = 250,
): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout> | undefined

  return (...args: A) => {
    if (timer !== undefined) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), wait)
  }
}

/** True when the visitor has asked for reduced motion. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Trap focus inside a container (drawers, modals).
 *
 * Without this, tabbing out of an open drawer lands on the page behind it,
 * which is disorienting with a screen reader and fails WCAG 2.4.3.
 */
export function trapFocus(container: HTMLElement): () => void {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

  const handler = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return

    const items = $$<HTMLElement>(FOCUSABLE, container).filter((n) => n.offsetParent !== null)
    if (items.length === 0) return

    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  container.addEventListener('keydown', handler)

  return () => container.removeEventListener('keydown', handler)
}

/** Format cents as a display price, matching PHP's money() helper. */
export function money(cents: number): string {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`
}
